import { useCallback, useMemo } from 'react'
import type { IRole } from '@/modules/Role/Role'
import { useAuthContext } from '@/providers/Global'
import { hasBaseRole } from '@/utils/authorities'
import type { TPaginateResults } from '@/utils/types'

import { useSWRAxios } from './useSWRAxios'

//  //////////////////////////////////////////////
//  MARK: EXPORTS
export function useRoles(): {
  loading: boolean
  roles: IRole[]
  authorities: Set<string>
  hasAuthority(authority: string): boolean
  menuItems: Set<string> & {
    has(value: string, ignoreSuperadmin?: boolean): boolean
  }
} {
  const Auth = useAuthContext()

  // /api/role est paginé (défaut max=10, plafond serveur 250) : sans max explicite,
  // les rôles au-delà du 10e (ex. ROLE_COMMS) sont tronqués et leurs menu items
  // n'apparaissent jamais. Invisible en superadmin (has() court-circuite à true).
  // Un compte sans rôle de base (client CAMO, NAA) n'appelle pas /api/role, qui lui est fermé : ses menus
  // viennent des rôles portés par son profil.
  const baseRole = hasBaseRole(Auth.active?.roles)
  const { data, error } = useSWRAxios<TPaginateResults<IRole>>(
    baseRole ? '/api/role?max=250' : false,
  )
  const profileRoles = Auth.active?.user?.roles

  const roles = useMemo<IRole[]>(
    () => (baseRole && !error ? (data?.data.data ?? []) : (profileRoles ?? [])),
    [baseRole, error, data?.data.data, profileRoles],
  )

  const authorities = useMemo(() => new Set(Auth.active?.roles ?? []), [Auth.active?.roles])

  const hasAuthority = useCallback(
    (authority: string) => authorities.has(authority) || authorities.has('ROLE_SUPERADMIN'),
    [authorities],
  )

  const menuItems = useMemo(() => {
    const value = new Set<string>()

    for (const role of roles) {
      if (authorities.has(role.authority)) {
        for (const menuItem of role.menuItems) {
          value.add(menuItem.code)
        }
      }
    }

    return {
      ...value,
      has: (v: string, ignoreSuperadmin?: boolean) => {
        if (!ignoreSuperadmin && authorities.has('ROLE_SUPERADMIN')) {
          return true
        }

        return value.has(v)
      },
    }
  }, [authorities, roles])

  return {
    loading: baseRole && !data && !error,
    roles,
    authorities,
    hasAuthority,
    menuItems,
  }
}
